/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: <bl-product-media> (used by snippets/bl_product-media.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Extends the theme's <product-media> element to support the "Grid" desktop media layout of the
 * "Product page (BL)" section. In that layout the main carousel uses Flickity's "watchCSS" option, so
 * Flickity deactivates itself on desktop (where the media is laid out as a CSS grid) and stays active
 * on mobile. The base element assumes an always-active carousel in three places, guarded here.
 */
if (window.customElements.get('product-media') && !window.customElements.get('bl-product-media')) {
  const ThemeProductMedia = window.customElements.get('product-media');

  class BLProductMedia extends ThemeProductMedia {
    async connectedCallback() {
      super.connectedCallback();

      const carousel = this.querySelector('flickity-carousel');

      if (!carousel || carousel.childElementCount === 1) {
        return;
      }

      // When Flickity deactivates itself ("watchCSS", i.e. resizing to the desktop grid), it removes the
      // "is-selected" class from the selected cell. The zoom feature and the grid CSS rely on that class,
      // so re-apply it to the last selected media.
      const flickityInstance = await carousel.flickityInstance;

      flickityInstance.on('deactivate', () => {
        if (!flickityInstance.selectedElement) {
          return;
        }

        this.querySelectorAll('.product__media-item.is-selected').forEach((item) => item.classList.remove('is-selected'));
        flickityInstance.selectedElement.classList.add('is-selected');
      });
    }

    async _onFlickityReady() {
      const flickityInstance = await this.mainCarousel.flickityInstance;

      // The base implementation reads flickityInstance.selectedElement, which does not exist while
      // Flickity is deactivated (desktop grid layout)
      if (flickityInstance.isActive) {
        return super._onFlickityReady();
      }
    }

    async _setupVisibility() {
      const flickityInstance = await this.mainCarousel.flickityInstance;

      if (!flickityInstance || flickityInstance.isActive) {
        return super._setupVisibility();
      }

      // The base reveal animation reads flickityInstance.selectedElement, which does not exist while
      // Flickity is deactivated (desktop grid layout, where all media are visible at once). Without
      // this, [reveal] elements (selected image and thumbnails) would stay at opacity 0 forever.
      await this.untilVisible();

      this.querySelectorAll('[reveal]').forEach((element) => element.removeAttribute('reveal'));
    }
  }

  window.customElements.define('bl-product-media', BLProductMedia);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: <bl-product-addons> (used by snippets/bl_product-addons.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Powers the "Product Add On" block of the "Product page (BL)" section. The main product form submit is
 * intercepted (capture phase, so it runs before the theme's own submit handler): every checked add-on is
 * added to the cart first, then the submit is handed back to the theme. Because the theme's add-to-cart
 * request happens after the add-ons are in the cart, the notification, mini-cart and cart count it renders
 * already include the add-ons.
 *
 * Whenever an add-on is checked or unchecked the element announces it with a "bl:selection:changed" event on
 * the document, which <bl-atc-price> (below) listens for to add the checked add-ons to the button price.
 */
if (!window.customElements.get('bl-product-addons')) {
  class BLProductAddons extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._bypassNextSubmit = false;
      this._isProcessing = false;
      document.addEventListener('submit', this._onFormSubmit.bind(this), { capture: true, signal: this._abortController.signal });
      this.addEventListener('change', this._onAddonChanged.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    _onAddonChanged(event) {
      if (!event.target.matches('.bl-addon__input')) {
        return;
      }

      document.dispatchEvent(new CustomEvent('bl:selection:changed', { detail: { formId: this.getAttribute('form-id'), checked: event.target.checked } }));
    }

    get _checkedItems() {
      // Line item properties for add-on analytics: where the add-on was offered, next to which product, and
      // its 1-based position among the offered add-ons. Underscore keys stay hidden in the cart and at checkout.
      const inputs = Array.from(this.querySelectorAll('.bl-addon__input'));
      const source = this.classList.contains('bl-addons--stack') ? 'pdp_addon_stack' : 'pdp_addon';
      const anchor = this.getAttribute('data-anchor-handle') || '';

      return inputs
        .filter((input) => input.checked)
        .map((input) => ({
          id: parseInt(input.value, 10),
          quantity: 1,
          properties: { _ns_src: source, _ns_anchor: anchor, _ns_slot: String(inputs.indexOf(input) + 1) }
        }))
        .filter((item) => !Number.isNaN(item.id));
    }

    async _onFormSubmit(event) {
      const form = event.target;

      // getAttribute, not form.id: the form contains an <input name="id"> (the variant id) which shadows
      // the form's id property (HTMLFormElement named-property access), so form.id is that input element
      if (form.getAttribute('id') !== this.getAttribute('form-id')) {
        return;
      }

      // The submit we re-dispatch after adding the add-ons must go through to the theme
      if (this._bypassNextSubmit) {
        this._bypassNextSubmit = false;
        return;
      }

      // Nothing checked, or the form is invalid: let the theme handle the submit (and the validation UI)
      const items = this._checkedItems;

      if (items.length === 0 || !form.checkValidity()) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this._isProcessing) {
        return;
      }

      this._isProcessing = true;

      // Start the theme's button loader right away — the theme only starts it once its own submit
      // handler runs, which is after the add-ons request below, leaving the button unresponsive
      const submitButtons = Array.from(form.elements).filter((button) => button.type === 'submit');

      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute('disabled', 'disabled');
        submitButton.setAttribute('aria-busy', 'true');
      });

      try {
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });

        // Announced for assets/ns-addon-analytics.js, which owns every analytics publish
        if (response.ok) {
          this.dispatchEvent(new CustomEvent('ns:addons:added', { bubbles: true, detail: { items: items } }));
        }
      } catch (error) {
        // Even if the add-ons could not be added (e.g. just sold out), the main product must still be added
      }

      this._isProcessing = false;
      this._bypassNextSubmit = true;
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));

      // The theme's submit handler takes over the buttons from here; if it bailed out (e.g. the form
      // became invalid meanwhile), reset them so the button does not stay stuck in the loading state
      if (!form.checkValidity()) {
        submitButtons.forEach((submitButton) => {
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-busy');
        });
      }
    }
  }

  window.customElements.define('bl-product-addons', BLProductAddons);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: <bl-addon-stack> (used by snippets/bl_product-media.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The overlay layers of the "Product Add On" block's add-on stack. Lives inside the first product image's
 * wrapper and holds one hidden <img> per stack variant (the variant's custom.add_on_stack_image, authored at
 * the same aspect ratio as that photo). Whenever the add-on selection changes it shows the layers of the
 * checked add-ons and hides the rest, in metafield order, so several stack at once. Every layer's image is
 * fetched while the page loads (once the photo it sits on has arrived, at low priority) so an overlay paints
 * the moment its add-on is checked; until then the layers stay hidden.
 *
 * Reads the selection straight from the DOM, like <bl-atc-price>, so it also recomputes when the product
 * re-renders and this element is created again.
 *
 * It also drives the phone-only pinned media of the "Product page (BL)" section: the media's sticky wrapper
 * (.bl-product-media__sticky) is confined to .product__media, so the distance from the wrapper's bottom to the
 * add-on picker is measured into --bl-media-sticky-extend on .product__media, which the section's CSS uses to
 * lengthen that containing block. The pin then releases exactly when the picker reaches the media.
 */
if (!window.customElements.get('bl-addon-stack')) {
  class BLAddonStack extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener('bl:selection:changed', this._onSelectionChanged.bind(this), { signal: this._abortController.signal });
      this.refresh();
      this._preloadLayers();
      this._watchStickyRange();
    }

    disconnectedCallback() {
      this._abortController.abort();

      if (this._stickyRangeObserver) {
        this._stickyRangeObserver.disconnect();
      }
    }

    get formId() {
      return this.getAttribute('form-id');
    }

    _onSelectionChanged(event) {
      if (!event.detail || !event.detail.formId || event.detail.formId === this.formId) {
        this.refresh();

        if (event.detail && event.detail.checked) {
          this._showFirstImage();
        }
      }
    }

    /**
     * The overlay sits on the first image, so checking an add-on while the gallery shows another one brings the
     * carousel back to it. Goes through the carousel's own selectCell, which also moves the thumbnails. Does
     * nothing when the carousel is not running (the desktop grid layout shows every image) or already on image 1.
     */
    async _showFirstImage() {
      const carousel = this.closest('flickity-carousel');

      if (!carousel || !carousel.flickityInstance) {
        return;
      }

      const flickityInstance = await carousel.flickityInstance;

      if (flickityInstance.isActive && flickityInstance.selectedIndex !== 0) {
        carousel.select(0);
      }
    }

    /**
     * Keeps --bl-media-sticky-extend on .product__media at the distance from the bottom of the sticky media
     * wrapper to the top of the add-on picker (the add-on stack CSS in sections/bl_main-product.liquid). Measured
     * again whenever the media or the product info changes height: images loading, a variant change, an
     * accordion, a resize. Neither observed element changes size when the value is applied (it only lengthens
     * .product__media), so the observer cannot loop. Both rects are layout positions, unaffected by the wrapper
     * being pinned at the time. The wrapper's top margin is negative by the breathing band it paints above the
     * image, so it is added back to find where the wrapper's border box really ends at rest.
     */
    _watchStickyRange() {
      const mediaColumn = this.closest('.product__media');
      const stickyWrapper = this.closest('.bl-product-media__sticky');
      const addons = document.querySelector(`bl-product-addons[form-id="${this.formId}"]`);
      const productInfo = addons ? addons.closest('.product__info') : null;

      if (!mediaColumn || !stickyWrapper || !productInfo || !window.ResizeObserver) {
        return;
      }

      this._stickyRangeObserver = new ResizeObserver(() => {
        const wrapperMarginTop = parseFloat(window.getComputedStyle(stickyWrapper).marginTop) || 0;
        const wrapperBottom = mediaColumn.getBoundingClientRect().top + wrapperMarginTop + stickyWrapper.offsetHeight;
        const distance = addons.getBoundingClientRect().top - wrapperBottom;

        mediaColumn.style.setProperty('--bl-media-sticky-extend', `${Math.max(0, distance)}px`);
      });

      this._stickyRangeObserver.observe(stickyWrapper);
      this._stickyRangeObserver.observe(productInfo);
    }

    /**
     * Fetches every layer's image while the page is still loading, so checking an add-on paints its overlay at
     * once instead of waiting on the download. The layers stay hidden until checked: a hidden <img> still
     * downloads once it has a source. The fetch starts after the product photo the layers sit on has finished,
     * at low priority, so it never competes with that photo or anything else above the fold.
     */
    _preloadLayers() {
      const photo = this.parentElement ? this.parentElement.querySelector('img:not(.bl-addon-stack__layer)') : null;
      const preload = () => {
        this.querySelectorAll('.bl-addon-stack__layer').forEach((layer) => this._assignSources(layer, 'low'));
      };

      if (photo && !photo.complete) {
        photo.addEventListener('load', preload, { once: true, signal: this._abortController.signal });
        photo.addEventListener('error', preload, { once: true, signal: this._abortController.signal });
      } else {
        preload();
      }
    }

    /**
     * Moves a layer's sources out of its data attributes, once. No fetch priority is set when a layer is being
     * shown right now, so a click that beats the preload downloads at the browser's normal image priority.
     */
    _assignSources(layer, fetchPriority) {
      if (layer.hasAttribute('src')) {
        return;
      }

      if (fetchPriority) {
        layer.setAttribute('fetchpriority', fetchPriority);
      }

      if (layer.dataset.srcset) {
        layer.setAttribute('srcset', layer.dataset.srcset);
      }

      if (layer.dataset.sizes) {
        layer.setAttribute('sizes', layer.dataset.sizes);
      }

      layer.setAttribute('src', layer.dataset.src);
    }

    refresh() {
      const checkedIds = new Set(
        Array.from(document.querySelectorAll(`bl-product-addons[form-id="${this.formId}"] .bl-addon__input:checked`)).map((input) => input.value)
      );

      this.querySelectorAll('.bl-addon-stack__layer').forEach((layer) => {
        const show = checkedIds.has(layer.getAttribute('data-variant-id'));

        if (show) {
          this._assignSources(layer);
        }

        layer.hidden = !show;
      });
    }
  }

  window.customElements.define('bl-addon-stack', BLAddonStack);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: <bl-product-sticky-form> (used by snippets/bl_product-sticky-form.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Extends the theme's <product-sticky-form> for the bottom sticky bar of the "Product page (BL)" section:
 * - The bar hides when the footer is visible on all breakpoints (the theme only does this on mobile, as
 *   its own bar sits at the top on desktop).
 * - The cart notification offset is not needed (it clears a TOP bar), so it is always removed.
 * - The bar's button adds to cart by submitting the main product form (requestSubmit with the main add to cart
 *   button as submitter), so it goes through exactly the same path: the selected pack or variant, quantity,
 *   ticked add-ons (<bl-product-addons> intercepts that submit), the theme's AJAX add and the mini-cart opening.
 *   The warranty is attached by the cart itself, so it follows too. No scrolling. While the add is in flight
 *   the bar's button is disabled and aria-busy, which also blocks a double tap.
 * - If the main form is invalid (e.g. a required choice is missing) nothing is submitted: the bar scrolls back
 *   to the main product section and pulses the main add to cart button twice, so the validation message shows.
 */
if (window.customElements.get('product-sticky-form') && !window.customElements.get('bl-product-sticky-form')) {
  const ThemeProductStickyForm = window.customElements.get('product-sticky-form');

  class BLProductStickyForm extends ThemeProductStickyForm {
    connectedCallback() {
      super.connectedCallback();
      this.querySelector('[data-action="bl-scroll-to-product"]')?.addEventListener('click', this._onScrollToProduct.bind(this));
    }

    get hidden() {
      return this.hasAttribute('hidden');
    }

    set hidden(value) {
      this.toggleAttribute('hidden', value);
      document.documentElement.style.removeProperty('--cart-notification-offset');
    }

    _setupVisibilityObservers() {
      const paymentContainerElement = document.getElementById('MainPaymentContainer').closest('form'),
        footerElement = document.querySelector('.shopify-section--footer'),
        rootStyles = getComputedStyle(document.documentElement),
        stickyHeaderOffset = (parseInt(rootStyles.getPropertyValue('--header-height')) || 0) * (parseInt(rootStyles.getPropertyValue('--enable-sticky-header')) || 0) + (parseInt(rootStyles.getPropertyValue('--announcement-bar-height')) || 0) * (parseInt(rootStyles.getPropertyValue('--enable-sticky-announcement-bar')) || 0);

      this._isFooterVisible = this._isPaymentContainerPassed = false;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === footerElement) {
            this._isFooterVisible = entry.intersectionRatio > 0;
          }

          if (entry.target === paymentContainerElement) {
            const boundingRect = paymentContainerElement.getBoundingClientRect();
            this._isPaymentContainerPassed = entry.intersectionRatio === 0 && boundingRect.top + boundingRect.height <= stickyHeaderOffset;
          }
        });

        this.hidden = !this._isPaymentContainerPassed || this._isFooterVisible;
      }, { rootMargin: `-${stickyHeaderOffset}px 0px 0px 0px` });

      this.intersectionObserver.observe(paymentContainerElement);
      this.intersectionObserver.observe(footerElement);
    }

    _onScrollToProduct() {
      const form = document.getElementById(this.getAttribute('form-id')),
        mainAddToCartButton = form?.querySelector('[data-product-add-to-cart-button]'),
        stickyButton = this.querySelector('[data-action="bl-scroll-to-product"]');

      if (stickyButton?.hasAttribute('aria-busy')) {
        return;
      }

      // Valid form: submit it the way the main button does (the main button as submitter, so the theme and
      // <bl-product-addons> treat it as the main button's own submit)
      if (form && mainAddToCartButton && !mainAddToCartButton.disabled && form.checkValidity()) {
        stickyButton?.setAttribute('disabled', 'disabled');
        stickyButton?.setAttribute('aria-busy', 'true');
        form.requestSubmit(mainAddToCartButton);

        // The theme and <bl-product-addons> disable the main button for the whole add: release the bar's
        // button when the main one is free again (with a timeout in case the request never finishes)
        const release = () => {
          observer.disconnect();
          clearTimeout(timer);
          stickyButton?.removeAttribute('disabled');
          stickyButton?.removeAttribute('aria-busy');
        };

        const observer = new MutationObserver(() => {
          if (!mainAddToCartButton.hasAttribute('aria-busy') && !mainAddToCartButton.disabled) {
            release();
          }
        });

        const timer = setTimeout(release, 15000);

        if (!mainAddToCartButton.disabled) {
          // Nothing started (the submit was ignored): do not leave the bar stuck
          release();
        } else {
          observer.observe(mainAddToCartButton, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
        }

        return;
      }

      this._scrollToProduct();
    }

    _scrollToProduct() {
      const sectionElement = this.closest('.shopify-section'), mainAddToCartButton = document.getElementById('AddToCart');

      sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (!mainAddToCartButton) {
        return;
      }

      // Pulse the main add to cart button twice once the scroll has settled ("scrollend" where supported,
      // with a timer fallback)
      let hasPulsed = false;

      const pulse = () => {
        if (hasPulsed) {
          return;
        }

        hasPulsed = true;
        window.removeEventListener('scrollend', pulse);
        mainAddToCartButton.animate({ transform: ['scale(1)', 'scale(1.06)', 'scale(1)'] }, { duration: 420, iterations: 2, easing: 'ease-in-out' });
      };

      window.addEventListener('scrollend', pulse, { once: true });
      setTimeout(pulse, 1000);
    }
  }

  window.customElements.define('bl-product-sticky-form', BLProductStickyForm);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: <bl-bundle-upsell> (used by snippets/bl_product-bundle-upsell.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Powers the "Bundle Upsell" block of the "Product page (BL)" section. The first option is the product being
 * viewed, the rest are bundle products. Selecting a bundle points the main product form's variant "id" input
 * at the bundle's variant, so the theme's own <product-form> submit, the dynamic checkout button and the
 * "Product Add On" block all keep working unchanged and add the bundle instead of the current product.
 *
 * The theme rewrites that same input on every variant change (variant-picker sets form.id.value before it
 * dispatches "variant:changed"), so the override is re-applied on that event, and the first option's price,
 * struck price and "Save" badge are rewritten from the new variant at the same time. Nothing else on the page
 * is touched: <bl-atc-price> below reads the checked option when it needs the price.
 *
 * Both this element and <bl-product-addons> announce a change with "bl:selection:changed" on the document.
 */
if (!window.customElements.get('bl-bundle-upsell')) {
  /**
   * The theme's own formatMoney is module-scoped inside theme.js and not exposed, so its placeholder handling
   * is mirrored here. Follows currency_code_enabled the way the Liquid `money` / `money_with_currency` switch
   * does, and strips any markup a shop's money format may carry (e.g. <span class="money">) since the result
   * is written as text.
   */
  const blFormatMoney = (cents) => {
    const settings = window.themeVariables.settings;
    const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) || '{{amount}}';
    const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';

    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const parts = (number / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');
    };

    let value;

    switch (placeholder) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '');
  };

  class BLBundleUpsell extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();

      const mainOption = this.querySelector('[data-bl-bundle-main] [data-bl-bundle-option]');
      this._mainVariantId = mainOption ? mainOption.getAttribute('data-variant-id') : null;

      this.addEventListener('change', this._onOptionChanged.bind(this), { signal: this._abortController.signal });
      // Listened for on the document rather than the form: the form lives in the "Buy buttons" block, which may
      // sit after this block in the DOM, and the event bubbles up from it anyway
      document.addEventListener('variant:changed', this._onVariantChanged.bind(this), { signal: this._abortController.signal });

      this._sync();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get form() {
      return document.getElementById(this.getAttribute('form-id'));
    }

    get selectedOption() {
      return this.querySelector('[data-bl-bundle-option]:checked');
    }

    _onOptionChanged(event) {
      if (!event.target.matches('[data-bl-bundle-option]')) {
        return;
      }

      this._sync();
    }

    _onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;

      // getAttribute, not form.id: the form contains an <input name="id"> which shadows the form's id property
      if (!variant || !event.target.getAttribute || event.target.getAttribute('id') !== this.getAttribute('form-id')) {
        return;
      }

      this._mainVariantId = String(variant.id);

      const mainCard = this.querySelector('[data-bl-bundle-main]');

      if (mainCard) {
        const price = variant.price || 0, compare = variant.compare_at_price || 0, saving = compare - price;

        mainCard.querySelector('[data-bl-bundle-option]')?.setAttribute('data-variant-id', variant.id);

        const priceElement = mainCard.querySelector('[data-bl-bundle-price]'),
          compareElement = mainCard.querySelector('[data-bl-bundle-compare]'),
          badgeElement = mainCard.querySelector('[data-bl-bundle-badge]');

        if (priceElement) {
          priceElement.textContent = blFormatMoney(price);
        }

        if (compareElement) {
          compareElement.hidden = compare <= price;
          compareElement.textContent = blFormatMoney(compare);
        }

        if (badgeElement) {
          badgeElement.hidden = saving <= 0;
          badgeElement.textContent = (window.themeVariables.strings.collectionDiscount || '@savings@').replace('@savings@', blFormatMoney(saving));
        }
      }

      this._applyVariant();
    }

    _sync() {
      const selected = this.selectedOption;

      this.querySelectorAll('.bl-bundle__option').forEach((option) => {
        option.classList.toggle('is-selected', selected !== null && option.contains(selected));
      });

      this._applyVariant();
      this._writeOffer();

      document.dispatchEvent(new CustomEvent('bl:selection:changed', { detail: { formId: this.getAttribute('form-id') } }));
    }

    /**
     * Add-on analytics: tells the cart which offer the main line was bought through ("bundle:<handle>" or
     * "bundle:main"). When <bl-price-packs> shares the form it writes the offer instead, reading this selection.
     */
    _writeOffer() {
      const formId = this.getAttribute('form-id');

      if (!formId || document.querySelector('bl-price-packs[form-id="' + formId + '"]')) {
        return;
      }

      const selected = this.selectedOption,
        handle = selected && selected.value !== 'main' ? selected.getAttribute('data-bundle-handle') : null,
        values = { offer: handle ? 'bundle:' + handle : 'bundle:main', changed: handle ? '1' : '0' };

      [['offer', 'properties[_ns_offer]'], ['changed', 'properties[_ns_offer_changed]']].forEach(([key, name]) => {
        let input = this.querySelector('input[data-ns-offer="' + key + '"]');

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.setAttribute('form', formId);
          input.setAttribute('data-ns-offer', key);
          this.appendChild(input);
        }

        input.value = values[key];
      });
    }

    /**
     * Points the form at the selected option's variant. The theme's <product-form> posts its FormData as-is,
     * so swapping the value of the single "id" input is enough for the cart, the mini-cart refresh, the loader
     * button and the error handling to stay the theme's own.
     */
    _applyVariant() {
      const form = this.form, idInput = form ? form.querySelector('input[name="id"]') : null;

      if (!idInput) {
        return;
      }

      const selected = this.selectedOption,
        variantId = selected && selected.value !== 'main' ? selected.getAttribute('data-variant-id') : this._mainVariantId;

      if (variantId) {
        idInput.value = variantId;
      }
    }
  }

  /**
   * ----------------------------------------------------------------------------------------------------------
   * BL: <bl-atc-price> (used by snippets/bl_product-atc-content.liquid)
   * ----------------------------------------------------------------------------------------------------------
   *
   * The price inside the add to cart button (and the sticky bar's copy of it). Its "price" attribute is the
   * selected variant's price as rendered by Liquid; on top of that it reflects what the form will actually add:
   *
   *   - a bundle checked in <bl-bundle-upsell> REPLACES the variant price (its radio carries data-price);
   *   - every add-on checked in <bl-product-addons> is ADDED to it (each checkbox carries data-price).
   *
   * It reads that state straight from the DOM, so it does not care whether the other elements have been
   * upgraded yet or where they sit in the markup. The theme re-creates the button content on every variant
   * change, so a fresh instance connects with the new base price and simply recomputes — no variant event is
   * needed here. Both blocks announce user changes with "bl:selection:changed".
   */
  class BLAtcPrice extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener('bl:selection:changed', this._onSelectionChanged.bind(this), { signal: this._abortController.signal });
      this.refresh();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get formId() {
      return this.getAttribute('form-id');
    }

    _onSelectionChanged(event) {
      if (!event.detail || !event.detail.formId || event.detail.formId === this.formId) {
        this.refresh();
      }
    }

    /**
     * What the form will charge right now, in cents: the "price" attribute, replaced by a checked bundle,
     * plus every checked add-on. null without a form-id, so callers leave the Liquid-rendered text alone.
     */
    computeTotal() {
      const formId = this.formId;
      let total = parseInt(this.getAttribute('price'), 10) || 0;

      if (!formId) {
        return null;
      }

      const bundleOption = document.querySelector(`bl-bundle-upsell[form-id="${formId}"] [data-bl-bundle-option]:checked`);

      if (bundleOption && bundleOption.hasAttribute('data-price')) {
        total = parseInt(bundleOption.getAttribute('data-price'), 10) || 0;
      }

      document.querySelectorAll(`bl-product-addons[form-id="${formId}"] .bl-addon__input:checked`).forEach((input) => {
        total += parseInt(input.getAttribute('data-price'), 10) || 0;
      });

      return total;
    }

    refresh() {
      const total = this.computeTotal();

      if (total === null) {
        return;
      }

      this.textContent = blFormatMoney(total);
    }
  }

  /**
   * ----------------------------------------------------------------------------------------------------------
   * BL: <bl-installments> (used by snippets/bl_product-installments.liquid)
   * ----------------------------------------------------------------------------------------------------------
   *
   * One part of the button price split into equal payments — the "RM199" in "or 3 payments of RM199". Same
   * inputs and same listening as <bl-atc-price>, so whatever moves the button price (variant, pack, bundle,
   * add-on) moves this too; only the rendering differs. "count" is the number of payments, default 3.
   *
   * Each part is floored to a whole currency unit, so RM599 reads "3 payments of RM199", not RM199.67 — the
   * parts never add up to more than the price.
   */
  class BLInstallments extends BLAtcPrice {
    refresh() {
      const total = this.computeTotal();

      if (total === null) {
        return;
      }

      const count = parseInt(this.getAttribute('count'), 10) || 3;
      this.textContent = blFormatMoney(Math.floor(total / count / 100) * 100);
    }
  }

  /**
   * ----------------------------------------------------------------------------------------------------------
   * BL: <bl-product-price> (used by snippets/bl_product-info.liquid)
   * ----------------------------------------------------------------------------------------------------------
   *
   * The price list under the product title. It shows the same total as <bl-atc-price> — the "price" attribute
   * (the variant's price, or a pack total written by the packs blocks), replaced by a checked bundle, plus every
   * checked add-on — so the two can never disagree. Next to it sits the struck price, built the same way from
   * the "compare" attribute (the variant's compare-at price, or the pack's), replaced by a checked bundle's
   * data-compare-price, plus every checked add-on at its own price. It is only shown while it is above the
   * total, and the price swaps between the theme's sale and regular styles to match.
   *
   * Like <bl-atc-price>, it never has to be told about a variant change: the meta block it lives in is
   * re-rendered by the theme's product-rerender, so a fresh instance connects with the new variant's attributes.
   */
  class BLProductPrice extends BLAtcPrice {
    /**
     * What the selection would cost at full price, in cents: an item without a compare-at price counts at its
     * price. null without a form-id, like computeTotal.
     */
    computeCompareTotal() {
      const formId = this.formId;
      const price = parseInt(this.getAttribute('price'), 10) || 0;
      let total = Math.max(parseInt(this.getAttribute('compare'), 10) || 0, price);

      if (!formId) {
        return null;
      }

      const bundleOption = document.querySelector(`bl-bundle-upsell[form-id="${formId}"] [data-bl-bundle-option]:checked`);

      if (bundleOption && bundleOption.hasAttribute('data-price')) {
        const bundlePrice = parseInt(bundleOption.getAttribute('data-price'), 10) || 0;
        total = Math.max(parseInt(bundleOption.getAttribute('data-compare-price'), 10) || 0, bundlePrice);
      }

      document.querySelectorAll(`bl-product-addons[form-id="${formId}"] .bl-addon__input:checked`).forEach((input) => {
        total += parseInt(input.getAttribute('data-price'), 10) || 0;
      });

      return total;
    }

    refresh() {
      const total = this.computeTotal();

      if (total === null) {
        return;
      }

      const compare = this.computeCompareTotal(),
        onSale = compare > total,
        priceElement = this.querySelector('[data-bl-product-price]'),
        amountElement = this.querySelector('[data-bl-product-price-amount]'),
        compareElement = this.querySelector('[data-bl-product-compare]'),
        compareAmountElement = this.querySelector('[data-bl-product-compare-amount]');

      if (amountElement) {
        amountElement.textContent = blFormatMoney(total);
      }

      if (priceElement) {
        priceElement.classList.toggle('price--highlight', onSale);
        priceElement.classList.toggle('semi-bold', onSale);
        priceElement.classList.toggle('price--large', !onSale);
        priceElement.classList.toggle('bold', !onSale);
      }

      if (compareElement) {
        compareElement.hidden = !onSale;
      }

      if (compareAmountElement) {
        compareAmountElement.textContent = onSale ? blFormatMoney(compare) : '';
      }
    }
  }

  window.customElements.define('bl-bundle-upsell', BLBundleUpsell);

  if (!window.customElements.get('bl-atc-price')) {
    window.customElements.define('bl-atc-price', BLAtcPrice);
  }

  if (!window.customElements.get('bl-installments')) {
    window.customElements.define('bl-installments', BLInstallments);
  }

  if (!window.customElements.get('bl-product-price')) {
    window.customElements.define('bl-product-price', BLProductPrice);
  }
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * BL: cart drawer refresh delay
 * ------------------------------------------------------------------------------------------------------------
 *
 * The theme holds every re-render of the cart drawer for a fixed time after the cart has answered, so the line
 * item's spinner-to-tick animation can play: 750ms on a quantity change, 600ms on a removal and 600ms on an add
 * made from inside the drawer (cart-drawer.js and item-quantity.js in theme.js). Those waits are longer than
 * the animations behind them: the tick has finished at 450ms, a removed line has collapsed at 375ms and the
 * loader button has settled at 250ms. Every cart:refresh passes through here first (a capture listener on the
 * document runs on the way down to the element the event is dispatched on, before the drawer's own listener)
 * and the wait is capped at the loader button's length: the tick is cut short, and the drawer updates 200ms
 * sooner on every change, which the customer notices more than the end of the tick.
 */
document.addEventListener('cart:refresh', (event) => {
  const longestFeedback = 250;
  const cartDrawer = document.getElementById('mini-cart');

  if (cartDrawer && cartDrawer.nextReplacementDelay > longestFeedback) {
    cartDrawer.nextReplacementDelay = longestFeedback;
  }

  if (event.detail && event.detail.replacementDelay > longestFeedback) {
    event.detail.replacementDelay = longestFeedback;
  }
}, true);

/**
 * Shopify Inbox chat clears sticky add-to-cart bars.
 * Inbox reads --shopify-chat-activator-offset (set on :root by the app) for the button's bottom offset. While a
 * bottom-fixed sticky ATC bar is visible, raise it to sit 12px above the bar's button. Otherwise remove our inline
 * value so the app's own one applies. The same logic is inlined by snippets/bl_chat-clear.liquid for the landing layout.
 */
(function () {
  if (window.__blChatClear) return;
  window.__blChatClear = true;

  var VAR = '--shopify-chat-activator-offset';
  var GAP = 12;
  var BARS = 'bl-product-sticky-form:not([hidden]), product-sticky-form:not([hidden]), .lp-stick-nsfs2 .bar.show, .lp-sticky-cta.is-visible, .r-1gsj0i9:not(.hide-sticky)';
  var GENERIC = '[class*="sticky-atc"], [class*="sticky-cta"], [class*="sticky-form"], [class*="sticky-add"], [class*="stick-"] .bar';
  var BTNS = 'button, a.button, [role="button"], a[href]';
  var root = document.documentElement;
  var base = null;
  var overridden = false;
  var queued = false;
  var burst = 0;

  function visible(el) {
    var cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0.01;
  }

  // Top of the bar's visible button area (falls back to the bar itself when it has no button)
  function barTop(bar) {
    var vh = window.innerHeight;
    var top = null;
    var btns = bar.querySelectorAll(BTNS);
    for (var i = 0; i < btns.length; i++) {
      var r = btns[i].getBoundingClientRect();
      if (r.width < 24 || r.height < 24 || r.bottom <= 0 || r.top >= vh || !visible(btns[i])) continue;
      if (top === null || r.top < top) top = r.top;
    }
    if (top === null) {
      var br = bar.getBoundingClientRect();
      if (br.width > 0 && br.height > 0 && br.top < vh) top = br.top;
    }
    return top;
  }

  function neededSpace() {
    var vh = window.innerHeight;
    var list = document.querySelectorAll(BARS + ', ' + GENERIC);
    var need = 0;
    for (var i = 0; i < list.length; i++) {
      var bar = list[i];
      if (bar.hidden || !visible(bar)) continue;
      // Bottom-fixed bars only. A bar translated off-screen has its top at or below the viewport edge and drops out.
      var r = bar.getBoundingClientRect();
      if (getComputedStyle(bar).position !== 'fixed' || r.height === 0 || r.bottom < vh - 8 || r.top < vh * 0.4) continue;
      var top = barTop(bar);
      if (top === null || top >= vh) continue;
      need = Math.max(need, vh - top);
    }
    return need;
  }

  function update() {
    queued = false;
    if (!overridden) {
      var v = parseFloat(getComputedStyle(root).getPropertyValue(VAR));
      if (!isNaN(v)) base = v;
    }
    if (base === null) return; // Inbox is not on this page
    var need = neededSpace();
    if (need > 0) {
      root.style.setProperty(VAR, Math.ceil(Math.max(base, need + GAP)) + 'px');
      overridden = true;
    } else if (overridden) {
      root.style.removeProperty(VAR);
      if (!root.getAttribute('style')) root.removeAttribute('style');
      overridden = false;
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  // Keep recomputing for the length of a bar's slide-in or slide-out
  function follow() {
    burst = Date.now() + 700;
    (function tick() {
      update();
      if (Date.now() < burst) requestAnimationFrame(tick);
    })();
  }

  var anyBar = BARS + ', ' + GENERIC;
  function inBar(node) { return node && node.nodeType === 1 && node.closest && node.closest(anyBar); }

  function touchesBar(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType === 1 && (n.matches(anyBar) || n.querySelector(anyBar))) return true;
    }
    return false;
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', schedule);
    window.visualViewport.addEventListener('scroll', schedule);
  }
  ['transitionrun', 'transitionend', 'animationend'].forEach(function (name) {
    document.addEventListener(name, function (e) {
      if (!inBar(e.target)) return;
      if (name === 'transitionrun') follow(); else schedule();
    }, true);
  });

  // Bars appear through the hidden attribute, a class or an inline style, and may be re-rendered (product-rerender)
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var m = muts[i];
      if (m.type === 'attributes' ? m.target !== root && inBar(m.target) : inBar(m.target) || touchesBar(m.addedNodes) || touchesBar(m.removedNodes)) {
        follow();
        return;
      }
    }
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });

  schedule();
  window.addEventListener('load', function () { setTimeout(schedule, 800); setTimeout(schedule, 3000); });
})();
